import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Calendar, Plus, Building2, CalendarX, Eye, EyeOff, Users } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export default function HotelInventoryPage() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [roomInventory, setRoomInventory] = useState([]);
  const [venueInventory, setVenueInventory] = useState([]);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [showVenueDialog, setShowVenueDialog] = useState(false);
  const [showBlackoutDialog, setShowBlackoutDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Date picker states
  const [roomDate, setRoomDate] = useState(null);
  const [roomStartDate, setRoomStartDate] = useState(null);
  const [roomEndDate, setRoomEndDate] = useState(null);
  const [blackoutDate, setBlackoutDate] = useState(null);
  const [blackoutStartDate, setBlackoutStartDate] = useState(null);
  const [blackoutEndDate, setBlackoutEndDate] = useState(null);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchInventory();
    }
  }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const res = await api.getVendorProperties();
      setProperties(res.data);
      if (res.data.length > 0) {
        setSelectedProperty(res.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInventory = async () => {
    if (!selectedProperty) return;
    try {
      const [roomsRes, venuesRes, blackoutRes] = await Promise.all([
        api.getRoomInventory(selectedProperty.id),
        api.getVenueInventory(selectedProperty.id),
        api.getBlackoutDates(selectedProperty.id)
      ]);
      setRoomInventory(roomsRes.data);
      setVenueInventory(venuesRes.data);
      setBlackoutDates(blackoutRes.data);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
    }
  };

  const handleAddRoomInventory = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.createRoomInventory({
        property_id: selectedProperty.id,
        date: formData.get('date'),
        room_category: formData.get('room_category'),
        total_rooms: parseInt(formData.get('total_rooms')),
        available_rooms: parseInt(formData.get('available_rooms')),
        price_per_room: parseFloat(formData.get('price_per_room')),
        min_nights: parseInt(formData.get('min_nights') || 1),
        visibility: formData.get('visibility')
      });
      toast.success('Room inventory added');
      setShowRoomDialog(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add inventory');
    }
  };

  const handleBulkUpdate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.bulkUpdateRoomInventory({
        property_id: selectedProperty.id,
        room_category: formData.get('room_category'),
        start_date: formData.get('start_date'),
        end_date: formData.get('end_date'),
        total_rooms: parseInt(formData.get('total_rooms')),
        available_rooms: parseInt(formData.get('available_rooms')),
        price_per_room: parseFloat(formData.get('price_per_room')),
        visibility: formData.get('visibility')
      });
      toast.success('Bulk inventory updated');
      setShowBulkDialog(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update inventory');
    }
  };

  const handleAddVenue = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.createVenueSlot({
        property_id: selectedProperty.id,
        date: formData.get('date'),
        venue_name: formData.get('venue_name'),
        venue_type: formData.get('venue_type'),
        capacity: parseInt(formData.get('capacity')),
        time_slot: formData.get('time_slot'),
        price: parseFloat(formData.get('price')),
        setup_options: formData.get('setup_options').split(',').map(s => s.trim()),
        av_included: formData.get('av_included') === 'on',
        visibility: formData.get('visibility')
      });
      toast.success('Venue slot added');
      setShowVenueDialog(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add venue');
    }
  };

  const handleAddBlackout = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.createBlackoutDates({
        property_id: selectedProperty.id,
        start_date: formData.get('start_date'),
        end_date: formData.get('end_date'),
        reason: formData.get('reason')
      });
      toast.success('Blackout dates added');
      setShowBlackoutDialog(false);
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add blackout dates');
    }
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'private': return <EyeOff className="w-4 h-4 text-slate-700" />;
      case 'verified_clients': return <Eye className="w-4 h-4 text-amber-500" />;
      case 'all_clients': return <Eye className="w-4 h-4 text-emerald-500" />;
      default: return null;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      available: 'bg-emerald-100 text-emerald-800',
      limited: 'bg-amber-100 text-amber-800',
      sold_out: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  if (!user || user.role !== 'vendor') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Access Denied</h2>
          <p className="text-slate-800 mb-4">Only vendors can access inventory management</p>
          <Link to="/"><Button>Go Home</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard/vendor">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
            </Link>
            <h1 className="luxury-text text-xl font-semibold text-midnight-navy">Inventory Management</h1>
          </div>
          {selectedProperty && (
            <Select
              value={selectedProperty.id}
              onValueChange={(id) => setSelectedProperty(properties.find(p => p.id === id))}
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {properties.map(prop => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </nav>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-midnight-navy"></div>
        </div>
      ) : properties.length === 0 ? (
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center py-12 bg-white rounded-lg border">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-800" />
            <p className="text-slate-800 mb-4">No properties found. Add a property first.</p>
            <Link to="/dashboard/vendor">
              <Button>Go to Dashboard</Button>
            </Link>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3 mb-8">
            <Button onClick={() => setShowBulkDialog(true)} className="bg-midnight-navy">
              <Calendar className="w-4 h-4 mr-2" /> Bulk Update Rooms
            </Button>
            <Button onClick={() => setShowRoomDialog(true)} variant="outline">
              <Plus className="w-4 h-4 mr-2" /> Add Room Inventory
            </Button>
            <Button onClick={() => setShowVenueDialog(true)} variant="outline">
              <Plus className="w-4 h-4 mr-2" /> Add Venue Slot
            </Button>
            <Button onClick={() => setShowBlackoutDialog(true)} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
              <CalendarX className="w-4 h-4 mr-2" /> Add Blackout Dates
            </Button>
          </div>

          {/* Privacy Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <h3 className="font-semibold text-blue-800 mb-2">🔒 Inventory Privacy Levels</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <EyeOff className="w-4 h-4 text-slate-700" />
                <span><strong>Private:</strong> Only you can see</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-amber-500" />
                <span><strong>Verified Clients:</strong> Corporate verified clients</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-emerald-500" />
                <span><strong>All Clients:</strong> All registered clients</span>
              </div>
            </div>
          </div>

          <Tabs defaultValue="rooms">
            <TabsList>
              <TabsTrigger value="rooms">Room Inventory</TabsTrigger>
              <TabsTrigger value="venues">Venue Slots</TabsTrigger>
              <TabsTrigger value="blackout">Blackout Dates</TabsTrigger>
            </TabsList>

            <TabsContent value="rooms" className="mt-6">
              {roomInventory.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-800" />
                  <p className="text-slate-800">No room inventory. Add inventory to get started.</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium">Date</th>
                        <th className="text-left px-4 py-3 text-sm font-medium">Category</th>
                        <th className="text-center px-4 py-3 text-sm font-medium">Total</th>
                        <th className="text-center px-4 py-3 text-sm font-medium">Available</th>
                        <th className="text-center px-4 py-3 text-sm font-medium">Booked</th>
                        <th className="text-right px-4 py-3 text-sm font-medium">Price/Room</th>
                        <th className="text-center px-4 py-3 text-sm font-medium">Status</th>
                        <th className="text-center px-4 py-3 text-sm font-medium">Visibility</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {roomInventory.slice(0, 30).map((slot) => (
                        <tr key={slot.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-sm">{new Date(slot.date).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-sm capitalize">{slot.room_category}</td>
                          <td className="px-4 py-3 text-sm text-center">{slot.total_rooms}</td>
                          <td className="px-4 py-3 text-sm text-center font-medium">{slot.available_rooms}</td>
                          <td className="px-4 py-3 text-sm text-center">{slot.booked_rooms}</td>
                          <td className="px-4 py-3 text-sm text-right">₹{slot.price_per_room.toLocaleString()}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(slot.status)}`}>
                              {slot.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {getVisibilityIcon(slot.visibility)}
                              <span className="text-xs capitalize">{slot.visibility.replace('_', ' ')}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {roomInventory.length > 30 && (
                    <div className="p-4 text-center text-sm text-slate-800 border-t">
                      Showing 30 of {roomInventory.length} entries
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="venues" className="mt-6">
              {venueInventory.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border">
                  <Users className="w-12 h-12 mx-auto mb-4 text-slate-800" />
                  <p className="text-slate-800">No venue slots. Add venue availability to get started.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {venueInventory.map((venue) => (
                    <div key={venue.id} className="bg-white rounded-lg border p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-medium">{venue.venue_name}</h3>
                          <p className="text-sm text-slate-800 capitalize">{venue.venue_type}</p>
                        </div>
                        {getVisibilityIcon(venue.visibility)}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-slate-800">Date:</span>
                          <span>{new Date(venue.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-800">Time:</span>
                          <span className="capitalize">{venue.time_slot.replace('_', ' ')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-800">Capacity:</span>
                          <span>{venue.capacity} pax</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-800">Price:</span>
                          <span className="font-medium">₹{venue.price.toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${venue.is_booked ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                          {venue.is_booked ? 'Booked' : 'Available'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="blackout" className="mt-6">
              {blackoutDates.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border">
                  <CalendarX className="w-12 h-12 mx-auto mb-4 text-slate-800" />
                  <p className="text-slate-800">No blackout dates configured.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {blackoutDates.map((blackout) => (
                    <div key={blackout.id} className="bg-white rounded-lg border p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{blackout.reason}</p>
                        <p className="text-sm text-slate-800">
                          {new Date(blackout.start_date).toLocaleDateString()} - {new Date(blackout.end_date).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                        Blocked
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* Add Room Dialog */}
      <Dialog open={showRoomDialog} onOpenChange={setShowRoomDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Room Inventory</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddRoomInventory} className="space-y-4">
            <div>
              <Label>Date</Label>
              <DatePicker
                selected={roomDate}
                onChange={(date) => setRoomDate(date)}
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                placeholderText="DD/MM/YYYY"
                className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                calendarClassName="custom-calendar"
                wrapperClassName="w-full"
                required
              />
              <input type="hidden" name="date" value={roomDate ? roomDate.toISOString().split('T')[0] : ''} />
            </div>
            <div>
              <Label>Room Category</Label>
              <Select name="room_category" defaultValue="deluxe">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="deluxe">Deluxe</SelectItem>
                  <SelectItem value="suite">Suite</SelectItem>
                  <SelectItem value="executive_suite">Executive Suite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Rooms</Label>
                <Input type="number" name="total_rooms" min="1" required />
              </div>
              <div>
                <Label>Available Rooms</Label>
                <Input type="number" name="available_rooms" min="0" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price per Room (₹)</Label>
                <Input type="number" name="price_per_room" min="0" step="100" required />
              </div>
              <div>
                <Label>Min Nights</Label>
                <Input type="number" name="min_nights" min="1" defaultValue="1" />
              </div>
            </div>
            <div>
              <Label>Visibility</Label>
              <Select name="visibility" defaultValue="private">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private (Only You)</SelectItem>
                  <SelectItem value="verified_clients">Verified Corporate Clients</SelectItem>
                  <SelectItem value="all_clients">All Registered Clients</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-midnight-navy">Add Inventory</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Update Room Inventory</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBulkUpdate} className="space-y-4">
            <div>
              <Label>Room Category</Label>
              <Select name="room_category" defaultValue="deluxe">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="deluxe">Deluxe</SelectItem>
                  <SelectItem value="suite">Suite</SelectItem>
                  <SelectItem value="executive_suite">Executive Suite</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <DatePicker
                  selected={roomStartDate}
                  onChange={(date) => setRoomStartDate(date)}
                  selectsStart
                  startDate={roomStartDate}
                  endDate={roomEndDate}
                  minDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                  required
                />
                <input type="hidden" name="start_date" value={roomStartDate ? roomStartDate.toISOString().split('T')[0] : ''} />
              </div>
              <div>
                <Label>End Date</Label>
                <DatePicker
                  selected={roomEndDate}
                  onChange={(date) => setRoomEndDate(date)}
                  selectsEnd
                  startDate={roomStartDate}
                  endDate={roomEndDate}
                  minDate={roomStartDate || new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                  required
                />
                <input type="hidden" name="end_date" value={roomEndDate ? roomEndDate.toISOString().split('T')[0] : ''} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Total Rooms</Label>
                <Input type="number" name="total_rooms" min="1" required />
              </div>
              <div>
                <Label>Available Rooms</Label>
                <Input type="number" name="available_rooms" min="0" required />
              </div>
            </div>
            <div>
              <Label>Price per Room (₹)</Label>
              <Input type="number" name="price_per_room" min="0" step="100" required />
            </div>
            <div>
              <Label>Visibility</Label>
              <Select name="visibility" defaultValue="private">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private (Only You)</SelectItem>
                  <SelectItem value="verified_clients">Verified Corporate Clients</SelectItem>
                  <SelectItem value="all_clients">All Registered Clients</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-midnight-navy">Update All Dates</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Venue Dialog */}
      <Dialog open={showVenueDialog} onOpenChange={setShowVenueDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Venue Slot</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddVenue} className="space-y-4">
            <div>
              <Label>Venue Name</Label>
              <Input name="venue_name" placeholder="Grand Ballroom" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Venue Type</Label>
                <Select name="venue_type" defaultValue="ballroom">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ballroom">Ballroom</SelectItem>
                    <SelectItem value="conference">Conference Room</SelectItem>
                    <SelectItem value="boardroom">Boardroom</SelectItem>
                    <SelectItem value="outdoor">Outdoor</SelectItem>
                    <SelectItem value="exhibition">Exhibition Hall</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <DatePicker
                  selected={blackoutDate}
                  onChange={(date) => setBlackoutDate(date)}
                  minDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                  required
                />
                <input type="hidden" name="date" value={blackoutDate ? blackoutDate.toISOString().split('T')[0] : ''} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Time Slot</Label>
                <Select name="time_slot" defaultValue="full_day">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_day">Full Day</SelectItem>
                    <SelectItem value="morning">Morning (8AM-1PM)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (2PM-6PM)</SelectItem>
                    <SelectItem value="evening">Evening (7PM-11PM)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Capacity</Label>
                <Input type="number" name="capacity" min="1" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price (₹)</Label>
                <Input type="number" name="price" min="0" step="1000" required />
              </div>
              <div>
                <Label>Setup Options</Label>
                <Input name="setup_options" placeholder="Theatre, Classroom, U-shape" />
              </div>
            </div>
            <div>
              <Label>Visibility</Label>
              <Select name="visibility" defaultValue="private">
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private</SelectItem>
                  <SelectItem value="verified_clients">Verified Clients</SelectItem>
                  <SelectItem value="all_clients">All Clients</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-midnight-navy">Add Venue</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Blackout Dialog */}
      <Dialog open={showBlackoutDialog} onOpenChange={setShowBlackoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Blackout Dates</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddBlackout} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <DatePicker
                  selected={blackoutStartDate}
                  onChange={(date) => setBlackoutStartDate(date)}
                  selectsStart
                  startDate={blackoutStartDate}
                  endDate={blackoutEndDate}
                  minDate={new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                  required
                />
                <input type="hidden" name="start_date" value={blackoutStartDate ? blackoutStartDate.toISOString().split('T')[0] : ''} />
              </div>
              <div>
                <Label>End Date</Label>
                <DatePicker
                  selected={blackoutEndDate}
                  onChange={(date) => setBlackoutEndDate(date)}
                  selectsEnd
                  startDate={blackoutStartDate}
                  endDate={blackoutEndDate}
                  minDate={blackoutStartDate || new Date()}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="DD/MM/YYYY"
                  className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none cursor-pointer bg-white"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                  required
                />
                <input type="hidden" name="end_date" value={blackoutEndDate ? blackoutEndDate.toISOString().split('T')[0] : ''} />
              </div>
            </div>
            <div>
              <Label>Reason</Label>
              <Input name="reason" placeholder="Private Event, Renovation, etc." required />
            </div>
            <p className="text-sm text-slate-800">
              All room inventory in this date range will be marked as sold out.
            </p>
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700">Block Dates</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
